// src/components/reports/preview/DailySummaryPreview.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDuration } from '../../../models/TimeEntryReportModel';

/**
 * Preview component for Daily Summary report type
 * @param {Object} props - Component props
 * @param {Array} props.data - Report data entries
 * @param {boolean} props.isPreview - Whether this is a limited preview
 * @param {number} props.previewLimit - Maximum number of rows to show in preview
 */
const DailySummaryPreview = ({ data, isPreview = true, previewLimit = 10 }) => {
  const { t } = useTranslation('reports');
  
  // Determine how many rows to display
  const displayData = isPreview ? data.slice(0, previewLimit) : data;
  
  // Calculate totals for all entries
  const totalPresence = data.reduce((sum, entry) => sum + entry.presenceDuration, 0);
  const totalBreak = data.reduce((sum, entry) => sum + entry.breakDuration, 0);
  const totalWork = data.reduce((sum, entry) => sum + entry.workDuration, 0);
  
  return (
    <div className="report-preview daily-summary-preview">
      <div className="report-preview-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th className="date-col">{t('date')}</th>
              <th className="staff-col">{t('staff')}</th>
              <th className="duration-col">{t('presenceDuration')}</th>
              <th className="duration-col">{t('breakTime')}</th>
              <th className="duration-col">{t('workDuration')}</th>
            </tr>
          </thead>
          
          <tbody>
            {displayData.map((entry, index) => (
              <tr key={entry.id || index} className={index % 2 === 1 ? 'alternate-row' : ''}>
                <td>{formatDate(entry.date)}</td>
                <td>{entry.staffName}</td>
                <td>{formatDuration(entry.presenceDuration)}</td>
                <td>{formatDuration(entry.breakDuration)}</td>
                <td>{formatDuration(entry.workDuration)}</td>
              </tr>
            ))}
            
            {/* Show totals row */}
            <tr className="totals-row">
              <td><strong>{t('total')}</strong></td>
              <td><strong>{t('staff')}</strong></td>
              <td><strong>{formatDuration(totalPresence)}</strong></td>
              <td><strong>{formatDuration(totalBreak)}</strong></td>
              <td><strong>{formatDuration(totalWork)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Show message if there are more entries than shown in preview */}
      {isPreview && data.length > previewLimit && (
        <div className="preview-more-message">
          {t('andMoreEntries', { count: data.length - previewLimit })}
        </div>
      )}
    </div>
  );
};

export default DailySummaryPreview;